import { Constructor } from '@angular/cdk/table';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IImgMediaDto } from 'src/app/core/dtos/interfaces/i-img-media-dto';
import { IVideoMediaDto } from 'src/app/core/dtos/interfaces/i-video-media-dto';
import { compareArrays } from '../utils/array-utils';

type WithSponsoredSectionFormSectionType = {
  _initSponsoredSection(
    headline: string,
    standfirst: string,
    body: string,
    imgs: IImgMediaDto[],
    videos: IVideoMediaDto[],
    mainImage: IImgMediaDto,
    mainVideo: IVideoMediaDto
  ): void;
  _initSponsoredSectionValueChanges(
    getPreviousSponsoredSection: () => string | null,
    getPreviousSponsoredStandfirst: () => string | null,
    getPreviousBodyValue: () => string | null,
    getPreviousImagesValue: () => IImgMediaDto[] | null,
    getPreviousVideosValue: () => IVideoMediaDto[] | null,
    getPreviousMainImageValue: () => IImgMediaDto | null,
    getPreviousSponsoredMainVideoValue: () => IVideoMediaDto | null
  ): void;
  _buildSponsoredHeadline(): string;
  _buildSponsoredStandfirst(): string;
  _buildSponsoredBody(): string;
  _buildSponsoredBodyImages(): IImgMediaDto[];
  _buildSponsoredBodyVideos(): IVideoMediaDto[];
  _buildSponsoredMainImage(): IImgMediaDto;
  _buildSponsoredMainVideo(): IVideoMediaDto;
};

type SponsoredSectionFormSectionRequerimentsBase = {
  form: FormGroup;
  FORM_PATHS: {
    SPONSORED_HEADLINE: () => string;
    SPONSORED_STANDFIRST: () => string;
    SPONSORED_BODY: () => string;
    SPONSORED_IMGS: () => string;
    SPONSORED_VIDEOS: () => string;
    SPONSORED_MAIN_IMAGE: () => string;
    SPONSORED_MAIN_VIDEO: () => string;
  };
  destroy$: Subject<boolean>;
};

export function WithSponsoredSectionFormSection<
  TBase extends Constructor<SponsoredSectionFormSectionRequerimentsBase>
>(
  baseClass: TBase
): {
  new (...args: any[]): WithSponsoredSectionFormSectionType;
  prototype: WithSponsoredSectionFormSectionType;
} & TBase {
  return class WithSponsoredSectionFormSectionable extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    _initSponsoredSection(
      headline: string,
      standfirst: string,
      body: string,
      imgs: IImgMediaDto[],
      videos: IVideoMediaDto[],
      mainImage: IImgMediaDto,
      mainVideo: IVideoMediaDto
    ): void {
      this.form.get(this.FORM_PATHS.SPONSORED_HEADLINE()).setValue(headline);
      this.form
        .get(this.FORM_PATHS.SPONSORED_STANDFIRST())
        .setValue(standfirst);

      this.form.get(this.FORM_PATHS.SPONSORED_BODY()).setValue(body);

      if (imgs) {
        const imgsControl = this.form.get(this.FORM_PATHS.SPONSORED_IMGS());
        imgs.forEach((i) => {
          (imgsControl as FormArray).push(new FormControl(i));
        });
      }

      if (videos) {
        const videosControl = this.form.get(this.FORM_PATHS.SPONSORED_VIDEOS());
        videos.forEach((i) =>
          (videosControl as FormArray).push(new FormControl(i))
        );
      }

      let mainImageControl = this.form.get(
        this.FORM_PATHS.SPONSORED_MAIN_IMAGE()
      ) as FormArray<FormControl<IImgMediaDto>>;
      let mainVideoControl = this.form.get(
        this.FORM_PATHS.SPONSORED_MAIN_VIDEO()
      ) as FormArray<FormControl<IVideoMediaDto>>;
      if (mainImage) mainImageControl.push(new FormControl(mainImage));
      if (mainVideo) mainVideoControl.push(new FormControl(mainVideo));
    }

    _initSponsoredSectionValueChanges(
      getPreviousSponsoredSection: () => string | null,
      getPreviousSponsoredStandfirst: () => string | null,
      getPreviousBodyValue: () => string | null,
      getPreviousImagesValue: () => IImgMediaDto[] | null,
      getPreviousVideosValue: () => IVideoMediaDto[] | null,
      getPreviousMainImageValue: () => IImgMediaDto | null,
      getPreviousSponsoredMainVideoValue: () => IVideoMediaDto | null
    ): void {
      this.form
        .get(this.FORM_PATHS.SPONSORED_HEADLINE())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue === getPreviousSponsoredSection()) {
              this.form
                .get(this.FORM_PATHS.SPONSORED_HEADLINE())
                .markAsPristine();
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.SPONSORED_STANDFIRST())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue === getPreviousSponsoredStandfirst()) {
              this.form
                .get(this.FORM_PATHS.SPONSORED_STANDFIRST())
                .markAsPristine();
            }
          },
        });
      this.form
        .get(this.FORM_PATHS.SPONSORED_BODY())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue === getPreviousBodyValue()) {
              this.form.get(this.FORM_PATHS.SPONSORED_BODY()).markAsPristine();
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.SPONSORED_IMGS())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (compareArrays(newValue, getPreviousImagesValue())) {
              this.form.get(this.FORM_PATHS.SPONSORED_IMGS()).markAsPristine();
            } else {
              this.form.get(this.FORM_PATHS.SPONSORED_IMGS()).markAsDirty();
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.SPONSORED_VIDEOS())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (compareArrays(newValue, getPreviousVideosValue())) {
              this.form
                .get(this.FORM_PATHS.SPONSORED_VIDEOS())
                .markAsPristine();
            } else {
              this.form.get(this.FORM_PATHS.SPONSORED_VIDEOS()).markAsDirty();
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.SPONSORED_MAIN_IMAGE())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (compareArrays(newValue, [getPreviousMainImageValue()])) {
              this.form
                .get(this.FORM_PATHS.SPONSORED_MAIN_IMAGE())
                .markAsPristine();
            } else {
              this.form
                .get(this.FORM_PATHS.SPONSORED_MAIN_IMAGE())
                .markAsDirty();
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.SPONSORED_MAIN_VIDEO())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (
              compareArrays(newValue, [getPreviousSponsoredMainVideoValue()])
            ) {
              this.form
                .get(this.FORM_PATHS.SPONSORED_MAIN_VIDEO())
                .markAsPristine();
            } else {
              this.form
                .get(this.FORM_PATHS.SPONSORED_MAIN_VIDEO())
                .markAsDirty();
            }
          },
        });
    }

    _buildSponsoredHeadline(): string {
      return this.form.get(this.FORM_PATHS.SPONSORED_HEADLINE()).value;
    }

    _buildSponsoredStandfirst(): string {
      return this.form.get(this.FORM_PATHS.SPONSORED_STANDFIRST()).value;
    }

    _buildSponsoredBody(): string {
      return this.form.get(this.FORM_PATHS.SPONSORED_BODY()).value;
    }

    _buildSponsoredBodyImages(): IImgMediaDto[] {
      return this.form.get(this.FORM_PATHS.SPONSORED_IMGS()).value;
    }

    _buildSponsoredBodyVideos(): IVideoMediaDto[] {
      return this.form.get(this.FORM_PATHS.SPONSORED_VIDEOS()).value;
    }

    _buildSponsoredMainImage(): IImgMediaDto {
      return this.form.get(this.FORM_PATHS.SPONSORED_MAIN_IMAGE()).value
        ? this.form.get(this.FORM_PATHS.SPONSORED_MAIN_IMAGE()).value[0]
        : null;
    }

    _buildSponsoredMainVideo(): IVideoMediaDto {
      return this.form.get(this.FORM_PATHS.SPONSORED_MAIN_VIDEO()).value
        ? this.form.get(this.FORM_PATHS.SPONSORED_MAIN_VIDEO()).value[0]
        : null;
    }
  };
}
